import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ANONYMOUS_REPORT_STATUS_LABELS,
  AnonymousReportStatusSchema,
  type AnonymousReportStatus,
} from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { Input } from '@deepguide-ai/ui/input'
import { PageEmptyState } from '@deepguide-ai/ui/page-empty-state'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@deepguide-ai/ui/table'
import { TableEmptyState } from '@deepguide-ai/ui/table-empty-state'
import { TableSkeleton } from '@deepguide-ai/ui/table-skeleton'
import { AlertTriangle, Eye, Search } from 'lucide-react'
import { formatDate, formatTimestamp } from '@/lib/format'
import { DEFAULT_PAGE_SIZE } from '@/lib/pagination'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { TablePaginationFooter } from '@/components/ui/TablePaginationFooter'
import { AssigneeFilter } from '@/components/compliance/AssigneeFilter'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import ScreenataPageHeader from '@/components/compliance/ScreenataPageHeader'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/anonymous-reports'
)({
  staticData: { pageHeader: { title: 'Whistleblower Report', pad: false } },
  component: AnonymousReportsPage,
})

export function AnonymousReportsPage() {
  const { workspaceId } = Route.useParams()
  const navigate = useNavigate()
  const { isAdmin, isLoading } = useWorkspacePermissions(workspaceId)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<AnonymousReportStatus | 'all'>('all')
  const [assignee, setAssignee] = useState('all')
  const hasFilters =
    Boolean(search.trim()) || status !== 'all' || assignee !== 'all'
  const input = {
    page,
    pageSize,
    search: search.trim() || undefined,
    status: status === 'all' ? undefined : status,
    assignee: assignee === 'all' ? undefined : assignee,
  }
  const query = useQuery({
    ...trpc.anonymousReports.list.queryOptions(input),
    queryKey: [...trpc.anonymousReports.list.queryKey(input), { workspaceId }],
    enabled: isAdmin,
  })
  if (isLoading)
    return (
      <p role='status' className='p-6 text-sm'>
        Loading…
      </p>
    )
  if (!isAdmin)
    return (
      <p role='alert' className='p-6 text-sm'>
        Only workspace administrators can view anonymous reports.
      </p>
    )
  return (
    <div className='flex flex-col gap-4 px-6 py-6'>
      <ScreenataPageHeader
        title='Whistleblower Report'
        description='Review concerns submitted through your anonymous reporting channel.'
      />
      <div className='flex flex-wrap items-center gap-3'>
        <div className='relative w-60 max-w-full'>
          <Search
            className='text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2'
            aria-hidden='true'
          />
          <Input
            aria-label='Search reports'
            placeholder='Search reports...'
            value={search}
            maxLength={200}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            className='bg-card h-9 rounded-sm pl-8 text-sm'
          />
        </div>
        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(
              value === 'all' ? 'all' : AnonymousReportStatusSchema.parse(value)
            )
            setPage(1)
          }}
        >
          <SelectTrigger
            aria-label='Report status'
            className='bg-card h-9 w-40 rounded-sm text-sm'
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All statuses</SelectItem>
            {AnonymousReportStatusSchema.options.map((value) => (
              <SelectItem key={value} value={value}>
                {ANONYMOUS_REPORT_STATUS_LABELS[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <AssigneeFilter
          value={assignee}
          onChange={(value) => {
            setAssignee(value)
            setPage(1)
          }}
          className='w-40 text-sm'
        />
      </div>
      {query.isError ? (
        <div role='alert' className='text-destructive space-y-3 text-sm'>
          <p>Unable to load anonymous reports.</p>
          <Button variant='outline' size='sm' onClick={() => query.refetch()}>
            Try again
          </Button>
        </div>
      ) : query.isPending ? (
        <TableSkeleton
          rows={10}
          columns={6}
          columnWidths={['40%', 96, 120, 146, 146, 52]}
        />
      ) : !query.data?.items.length && hasFilters ? (
        <TableEmptyState
          message='No reports match the selected filters.'
          className='min-h-60'
        />
      ) : !query.data?.items.length ? (
        <PageEmptyState
          icon={AlertTriangle}
          title='No anonymous reports'
          description='Submitted concerns will appear here.'
          footnote='Reports do not include the reporter’s account or IP address.'
        />
      ) : (
        <section className='border-border bg-card w-full overflow-hidden rounded-sm border'>
          <Table className='min-w-[900px] table-fixed'>
            <TableHeader>
              <TableRow className='border-border bg-card hover:bg-card'>
                <TableHead className='font-body text-text-subtle h-10 w-[40%] px-3 text-[10px] font-semibold tracking-[1px] uppercase'>
                  Title
                </TableHead>
                <TableHead className='font-body text-text-subtle h-10 w-[120px] px-3 text-[10px] font-semibold tracking-[1px] uppercase'>
                  Status
                </TableHead>
                <TableHead className='font-body text-text-subtle h-10 px-3 text-[10px] font-semibold tracking-[1px] uppercase'>
                  Assignee
                </TableHead>
                <TableHead className='font-body text-text-subtle h-10 w-[170px] px-3 text-[10px] font-semibold tracking-[1px] uppercase'>
                  Submitted
                </TableHead>
                <TableHead className='font-body text-text-subtle h-10 w-[170px] px-3 text-[10px] font-semibold tracking-[1px] uppercase'>
                  Completed
                </TableHead>
                <TableHead className='font-body text-text-subtle h-10 w-[72px] px-3 text-center text-[10px] font-semibold tracking-[1px] uppercase'>
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.items.map((report) => (
                <TableRow
                  key={report.id}
                  className='border-border hover:bg-surface-subtle cursor-pointer'
                  onClick={() =>
                    navigate({
                      to: '/workspaces/$workspaceId/anonymous-reports/$reportId',
                      params: { workspaceId, reportId: report.id },
                    })
                  }
                >
                  <TableCell className='min-w-0 px-3 py-2.5'>
                    <div className='font-body text-foreground truncate text-[12px] font-medium'>
                      {report.reportContent}
                    </div>
                    {report.reportContent && (
                      <div className='font-body text-text-placeholder mt-0.5 truncate text-[10px]'>
                        {report.reportContent}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className='px-3 py-2.5'>
                    <Badge
                      className='font-body h-5 rounded-md border-0 px-2 py-0 text-[10px] font-semibold tracking-[0.5px] uppercase'
                      status={
                        report.status === 'RESOLVED'
                          ? 'green'
                          : report.status === 'IN_PROGRESS'
                            ? 'blue'
                            : 'default'
                      }
                    >
                      {ANONYMOUS_REPORT_STATUS_LABELS[report.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className='font-body text-text-body px-3 py-2.5 text-[11px]'>
                    {report.assignee ? (
                      <div className='flex min-w-0 items-center gap-2'>
                        <MemberAvatar person={report.assignee} size='sm' />
                        <span className='truncate'>
                          {report.assignee.name || report.assignee.email}
                        </span>
                      </div>
                    ) : (
                      'Unassigned'
                    )}
                  </TableCell>
                  <TableCell className='font-body text-muted-foreground px-3 py-2.5 text-[11px]'>
                    <time
                      dateTime={new Date(report.createdAt).toISOString()}
                      title={formatTimestamp(report.createdAt)}
                    >
                      {formatDate(report.createdAt)}
                    </time>
                  </TableCell>
                  <TableCell className='font-body text-muted-foreground px-3 py-2.5 text-[11px]'>
                    {report.completedAt ? (
                      <time
                        dateTime={new Date(report.completedAt).toISOString()}
                        title={formatTimestamp(report.completedAt)}
                      >
                        {formatDate(report.completedAt)}
                      </time>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className='px-3 py-2.5 text-center'>
                    <Button
                      asChild
                      variant='ghost'
                      size='icon'
                      className='text-text-subtle hover:bg-muted hover:text-foreground h-7 w-7 rounded-sm'
                    >
                      <Link
                        to='/workspaces/$workspaceId/anonymous-reports/$reportId'
                        params={{ workspaceId, reportId: report.id }}
                        aria-label='View report details'
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Eye className='h-3.5 w-3.5' />
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePaginationFooter
            page={page}
            pageSize={pageSize}
            totalItems={query.data.total}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size)
              setPage(1)
            }}
          />
        </section>
      )}
    </div>
  )
}
